const clean = (value) =>
  String(value ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const uniqCI = (values) => {
  const seen = new Set();
  const unique = [];
  for (const value of values || []) {
    const cleaned = clean(value);
    const key = cleaned.toLowerCase();
    if (!cleaned || seen.has(key)) continue;
    seen.add(key);
    unique.push(cleaned);
  }
  return unique;
};

const stringsFrom = (value) => {
  if (Array.isArray(value)) return value.flatMap(stringsFrom);
  if (value && typeof value === 'object') return Object.values(value).flatMap(stringsFrom);
  return clean(value) ? [clean(value)] : [];
};

export const buildTransferableSkillsLexicon = (formData = {}) => {
  const education = Array.isArray(formData.educationHistory)
    ? formData.educationHistory.filter((entry) => entry?.showOnResume !== false)
    : [];
  const workHistory = Array.isArray(formData.workHistory)
    ? formData.workHistory.filter((entry) => entry?.showOnResume !== false)
    : [];
  const volunteerWork = Array.isArray(formData.volunteerWork) ? formData.volunteerWork : [];
  const certifications = Array.isArray(formData.certifications) ? formData.certifications : [];

  const academics = uniqCI(education.flatMap((entry) => [
    entry?.degree,
    entry?.major,
    entry?.minor,
    ...(entry?.relevantCoursesBullets || []),
    ...(entry?.relevantCourses || []),
    ...(entry?.significantProjects || []),
  ]));

  const workExperience = uniqCI(workHistory.flatMap((entry) => [
    entry?.jobTitle,
    entry?.employer,
    ...(entry?.enhancedResponsibilities || []),
    ...(entry?.enhancedAccomplishments || []),
    ...(entry?.responsibilitiesBullets || []),
    ...(entry?.accomplishmentsBullets || []),
    entry?.responsibilities,
    entry?.keyAccomplishments,
  ]));

  const volunteerExperience = uniqCI(volunteerWork.flatMap((entry) => [
    entry?.role,
    entry?.organization,
    entry?.responsibilities,
  ]));

  const military = uniqCI([
    formData.militaryInfo?.militaryBranch,
    formData.militaryInfo?.militaryRank,
    formData.militaryInfo?.militaryOccupation,
    formData.militaryInfo?.militaryLocation,
  ]);

  const credentials = uniqCI([
    ...certifications.flatMap((entry) => [
      entry?.certificationName,
      entry?.certificationAuthority,
    ]),
    ...stringsFrom(formData.training),
  ]);

  const additionalExperience = uniqCI([
    ...stringsFrom(formData.organizations),
    ...stringsFrom(formData.languages),
    ...stringsFrom(formData.awards),
    ...stringsFrom(formData.interests),
  ]);

  const existingSkills = uniqCI([
    ...stringsFrom(formData.skills),
    ...stringsFrom(formData.selectedSkills),
    ...stringsFrom(formData.softSkills),
    ...stringsFrom(formData.competencies),
  ]);

  const signals = uniqCI([
    ...academics,
    ...workExperience,
    ...volunteerExperience,
    ...military,
    ...credentials,
    ...additionalExperience,
    ...existingSkills,
  ]);

  return {
    academics,
    workExperience,
    volunteerExperience,
    military,
    credentials,
    additionalExperience,
    existingSkills,
    signals,
    hasEducation: academics.length > 0,
    hasExperienceSignals: signals.length > 0,
  };
};

export { clean, uniqCI };
